import { useState, useMemo, useRef, useCallback, useEffect, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus, Lock, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  ConfirmDialog,
  Drawer,
  cn,
} from '../design'
import {
  createCalEvent,
  updateCalEvent,
  deleteCalEvent,
  type CalEvent,
  type CalEventType,
} from '../api/calendar'
import {
  listStories, updateStory, deleteStory,
  listStorySessions, addStorySession, updateStorySession, deleteStorySession,
} from '../api/stories'
import { listMyStories, type MyStory } from '../api/me'
import { lookupProjects, type ProjectLookup } from '../api/projects'
import { getAgenda, type Agenda, type AgendaItem } from '../api/agenda'
import { useAuth } from '../auth/AuthContext'
import { useEventAlarms, getEventLeadMin, setEventLeadMin } from '../hooks/useEventAlarms'

/**
 * The personal calendar: every project the user can see, their personal
 * events, their scheduled/due stories and their Google events, from one
 * GET /me/agenda call per visible range. Project chips filter client-side;
 * `?project=KEY` (what the old per-project tab now links to) starts
 * filtered to one project.
 */

const HOUR_HEIGHT = 64
const SNAP_MIN = 15
const MIN_BLOCK = 24      // px — keeps a 15-minute item grabbable
const VIEW_START_HOUR = 7
const VIEW_END_HOUR   = 21  // 9pm
const HOURS = Array.from({ length: VIEW_END_HOUR - VIEW_START_HOUR }, (_, i) => VIEW_START_HOUR + i)
const HEADER_H = 50
const ALLDAY_ROW_H = 20
const GRID_H = HOURS.length * HOUR_HEIGHT

const HIDDEN_KEY = 'atlas.calendar.hidden'
const MODE_KEY   = 'atlas.calendar.mode'

type ViewMode = 'week' | 'month'

/** 'p:<id>' | 'personal' | 'google' */
type SourceKey = string

function sourceOf(item: AgendaItem): SourceKey {
  if (item.kind === 'google') return 'google'
  return item.project ? `p:${item.project.id}` : 'personal'
}

function readHidden(): Set<SourceKey> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function invalidateSchedule(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['agenda'] })
  qc.invalidateQueries({ queryKey: ['stories'] })
  qc.invalidateQueries({ queryKey: ['me'] })
  qc.invalidateQueries({ queryKey: ['dashboard'] })
}

export default function Calendar() {
  const [params, setParams] = useSearchParams()
  const [mode, setModeState] = useState<ViewMode>(() => {
    try { return localStorage.getItem(MODE_KEY) === 'month' ? 'month' : 'week' } catch { return 'week' }
  })
  const [cursor, setCursor] = useState(() => {
    const d = params.get('date')
    return d ? new Date(d + 'T00:00:00') : new Date()
  })
  const [hidden, setHidden] = useState<Set<SourceKey>>(readHidden)
  const onlyKey = params.get('project')

  const projectsQ = useQuery({ queryKey: ['lookups', 'projects'], queryFn: lookupProjects, staleTime: 60_000 })
  const projects = projectsQ.data ?? []
  const onlyProject = onlyKey ? projects.find(p => p.key === onlyKey) ?? null : null

  function setMode(m: ViewMode) {
    setModeState(m)
    try { localStorage.setItem(MODE_KEY, m) } catch { /* private mode */ }
  }

  function persistHidden(next: Set<SourceKey>) {
    setHidden(next)
    try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next])) } catch { /* private mode */ }
  }

  // Leaving the ?project= view: fall back to the remembered chip selection.
  function clearOnly() {
    const next = new URLSearchParams(params)
    next.delete('project')
    setParams(next, { replace: true })
  }

  function toggle(key: SourceKey) {
    if (onlyKey) {
      // First click out of a single-project view shows just that project + the clicked source.
      const keep = new Set<SourceKey>([key, ...(onlyProject ? [`p:${onlyProject.id}`] : [])])
      const all: SourceKey[] = ['personal', 'google', ...projects.map(p => `p:${p.id}`)]
      persistHidden(new Set(all.filter(k => !keep.has(k))))
      clearOnly()
      return
    }
    const next = new Set(hidden)
    if (next.has(key)) next.delete(key); else next.add(key)
    persistHidden(next)
  }

  const isVisible = useCallback((item: AgendaItem): boolean => {
    if (onlyKey) return item.project?.key === onlyKey
    return !hidden.has(sourceOf(item))
  }, [hidden, onlyKey])

  const isOn = (key: SourceKey) =>
    onlyKey ? (onlyProject ? key === `p:${onlyProject.id}` : false) : !hidden.has(key)

  const defaultProjectId = onlyProject?.id ?? null
  const scrollRef = useRef<HTMLDivElement>(null)

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 flex items-center justify-between gap-4 border-b border-border bg-bg-elevated flex-wrap">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Calendar</h2>
          <p className="text-xs text-fg-subtle">
            {mode === 'week'
              ? 'Everything on your plate across projects. Drag to move, pull the bottom edge to resize, click an empty slot to add.'
              : 'Click a day to open its week.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <CursorNav cursor={cursor} setCursor={setCursor} mode={mode} />
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <ToggleBtn active={mode === 'week'}  onClick={() => setMode('week')}>Week</ToggleBtn>
            <ToggleBtn active={mode === 'month'} onClick={() => setMode('month')}>Month</ToggleBtn>
          </div>
        </div>
      </div>

      {/* Source filter */}
      <div className="px-6 py-2 flex items-center gap-1.5 flex-wrap border-b border-border bg-bg">
        <span className="text-[10px] uppercase tracking-wider text-fg-subtle mr-1">Show</span>
        <SourceChip label="Personal" color="#8a8f98" on={isOn('personal')} onClick={() => toggle('personal')} />
        {projects.map(p => (
          <SourceChip
            key={p.id}
            label={p.key}
            title={p.name}
            color={p.color ?? '#9aa0a6'}
            on={isOn(`p:${p.id}`)}
            onClick={() => toggle(`p:${p.id}`)}
          />
        ))}
        <SourceChip label="Google" color="#4285f4" on={isOn('google')} onClick={() => toggle('google')} />
        {onlyKey && (
          <button onClick={clearOnly} className="ml-2 text-xs text-accent hover:underline">
            Show all
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        {mode === 'week'
          ? <WeekGrid
              cursor={cursor}
              isVisible={isVisible}
              projects={projects}
              defaultProjectId={defaultProjectId}
              scrollRef={scrollRef}
              openEventId={params.get('event')}
            />
          : <MonthGrid
              cursor={cursor}
              isVisible={isVisible}
              onPickDay={(d) => { setCursor(d); setMode('week') }}
            />}
      </div>
    </div>
  )
}

// ─── Nav controls ─────────────────────────────────────────────────────────

function CursorNav({ cursor, setCursor, mode }: { cursor: Date; setCursor: (d: Date) => void; mode: ViewMode }) {
  const label = mode === 'week'
    ? `${weekStart(cursor).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — ${addDays(weekStart(cursor), 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  function step(dir: -1 | 1) {
    const next = new Date(cursor)
    if (mode === 'week') next.setDate(next.getDate() + dir * 7)
    else next.setMonth(next.getMonth() + dir)
    setCursor(next)
  }

  return (
    <div className="flex items-center gap-1">
      <button onClick={() => step(-1)} className="p-1 rounded hover:bg-bg-muted text-fg-muted">
        <ChevronLeft size={16} />
      </button>
      <div className="text-sm font-medium px-2 min-w-[140px] text-center font-mono">{label}</div>
      <button onClick={() => step(1)} className="p-1 rounded hover:bg-bg-muted text-fg-muted">
        <ChevronRight size={16} />
      </button>
      <button
        onClick={() => setCursor(new Date())}
        className="ml-2 text-xs px-2 py-1 rounded border border-border hover:bg-bg-muted"
      >
        Today
      </button>
    </div>
  )
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-xs font-medium',
        active ? 'bg-accent text-accent-fg' : 'bg-bg-elevated text-fg-muted hover:bg-bg-muted',
      )}
    >
      {children}
    </button>
  )
}

function SourceChip({ label, title, color, on, onClick }: {
  label: string; title?: string; color: string; on: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      title={title ?? label}
      aria-pressed={on}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
        on ? 'border-transparent text-fg' : 'border-border text-fg-subtle bg-transparent opacity-60 hover:opacity-100',
      )}
      style={on ? { backgroundColor: `${color}26` } : undefined}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: on ? color : 'transparent', boxShadow: on ? undefined : `inset 0 0 0 1.5px ${color}` }}
      />
      {label}
    </button>
  )
}

// ─── Shared data ──────────────────────────────────────────────────────────

function useAgenda(from: Date, toInclusive: Date) {
  return useQuery({
    queryKey: ['agenda', ymd(from), ymd(toInclusive)],
    queryFn: () => getAgenda({ from: ymd(from), to: ymd(toInclusive), include_google: true }),
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  })
}

/** One scheduled work session of a story, as the calendar draws it. */
type SessionRef = { id: number; start_at: string; end_at: string; count: number }
type StoryBlock = { itemId: string; story: MyStory; session: SessionRef }
/** What the story drawer edits: a block, or (from a due marker) just the story. */
type StoryTarget = { story: MyStory; session: SessionRef | null }

/** Items that sit in the timed grid vs. the all-day strip. */
function splitItems(items: AgendaItem[]) {
  const events: Array<CalEvent & { _color: string | null }> = []
  const stories: StoryBlock[] = []
  const google: AgendaItem[] = []
  const allDay: AgendaItem[] = []
  for (const it of items) {
    if (it.all_day) { allDay.push(it); continue }
    if (it.kind === 'event' && it.event) events.push({ ...it.event, _color: it.project?.color ?? it.color })
    else if (it.kind === 'story_scheduled' && it.story && it.session) {
      stories.push({ itemId: it.id, story: it.story, session: it.session })
    }
    else if (it.kind === 'google') google.push(it)
  }
  return { events, stories, google, allDay }
}

type Lane = { lane: number; lanes: number }

/**
 * Side-by-side columns for overlapping timed items, per day, across events,
 * stories and Google alike — stacked blocks can't be grabbed. Classic
 * interval packing: each cluster of transitively-overlapping items shares
 * one lane count. Keyed by AgendaItem.id.
 */
function layoutLanes(items: AgendaItem[]): Record<string, Lane> {
  const out: Record<string, Lane> = {}
  const byDay: Record<string, Array<{ id: string; s: number; e: number }>> = {}
  for (const it of items) {
    if (it.all_day) continue
    const s = parseDt(it.start_at).getTime()
    const e = it.end_at ? parseDt(it.end_at).getTime() : s + 60 * 60_000
    // Short items still take their rendered height, so pack by that.
    const eff = Math.max(e, s + (MIN_BLOCK / HOUR_HEIGHT) * 60 * 60_000)
    ;(byDay[it.start_at.slice(0, 10)] ??= []).push({ id: it.id, s, e: eff })
  }
  for (const list of Object.values(byDay)) {
    list.sort((a, b) => a.s - b.s || b.e - a.e)
    let cluster: Array<{ id: string; lane: number }> = []
    let laneEnds: number[] = []
    let clusterEnd = -Infinity
    const flush = () => {
      for (const c of cluster) out[c.id] = { lane: c.lane, lanes: laneEnds.length }
      cluster = []; laneEnds = []
    }
    for (const it of list) {
      if (it.s >= clusterEnd) flush()
      let lane = laneEnds.findIndex(end => end <= it.s)
      if (lane < 0) { lane = laneEnds.length; laneEnds.push(it.e) } else laneEnds[lane] = it.e
      cluster.push({ id: it.id, lane })
      clusterEnd = Math.max(clusterEnd === -Infinity ? it.e : clusterEnd, it.e)
    }
    flush()
  }
  return out
}

// ─── Week grid ────────────────────────────────────────────────────────────

function WeekGrid({
  cursor,
  isVisible,
  projects,
  defaultProjectId,
  scrollRef,
  openEventId,
}: {
  cursor: Date
  isVisible: (item: AgendaItem) => boolean
  projects: ProjectLookup[]
  defaultProjectId: number | null
  scrollRef: React.RefObject<HTMLDivElement | null>
  openEventId: string | null
}) {
  const qc = useQueryClient()
  const start = useMemo(() => weekStart(cursor), [cursor])
  const last  = useMemo(() => addDays(start, 6), [start])
  const days  = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start])
  const agendaKey = ['agenda', ymd(start), ymd(last)]

  const agendaQ = useAgenda(start, last)
  const items = useMemo(() => (agendaQ.data?.items ?? []).filter(isVisible), [agendaQ.data, isVisible])
  const { events, stories, google, allDay } = useMemo(() => splitItems(items), [items])
  const lanes = useMemo(() => layoutLanes(items), [items])

  // Scroll to the working day (or now) once per week shown.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const now = new Date()
    const hour = days.some(d => sameDay(d, now)) ? Math.max(VIEW_START_HOUR, now.getHours() - 1) : 8
    el.scrollTop = Math.max(0, (hour - VIEW_START_HOUR) * HOUR_HEIGHT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd(start)])

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { start_at?: string; end_at?: string | null } }) => updateCalEvent(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: agendaKey })
      const prev = qc.getQueryData<Agenda>(agendaKey)
      if (prev) {
        qc.setQueryData<Agenda>(agendaKey, {
          ...prev,
          items: prev.items.map(it => it.kind === 'event' && it.event?.id === id
            ? { ...it, ...patch, event: { ...it.event, ...patch } as CalEvent }
            : it),
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) qc.setQueryData(agendaKey, ctx.prev) },
    onSettled: () => qc.invalidateQueries({ queryKey: ['agenda'] }),
  })

  // Move / resize / remove one work session. `patch: null` removes it.
  const storyMut = useMutation({
    mutationFn: async ({ block, patch }: { block: StoryBlock; patch: SessionPatch | null }): Promise<void> => {
      if (patch) await updateStorySession(block.story.id, block.session.id, patch)
      else await deleteStorySession(block.story.id, block.session.id)
    },
    onMutate: async ({ block, patch }) => {
      await qc.cancelQueries({ queryKey: agendaKey })
      const prev = qc.getQueryData<Agenda>(agendaKey)
      if (prev) {
        qc.setQueryData<Agenda>(agendaKey, {
          ...prev,
          items: prev.items
            .filter(it => patch !== null || it.id !== block.itemId)
            .map(it => it.id === block.itemId && patch && it.session
              ? { ...it, ...patch, session: { ...it.session, ...patch } }
              : it),
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) qc.setQueryData(agendaKey, ctx.prev) },
    onSettled: () => invalidateSchedule(qc),
  })

  const [creating, setCreating] = useState<{ start: Date; end: Date } | null>(null)
  const [editingEvent, setEditingEvent] = useState<CalEvent | null>(null)
  const [editingStory, setEditingStory] = useState<StoryTarget | null>(null)
  const [pendingDeleteEvent, setPendingDeleteEvent] = useState<CalEvent | null>(null)

  // Deep link from the My Work agenda: /calendar?date=…&event=ID opens that
  // event's drawer once; after it is closed, clicks drive the drawer again.
  const [deepLinkDone, setDeepLinkDone] = useState(false)
  const deepLinkEvent = useMemo(() => {
    if (!openEventId || deepLinkDone) return null
    return agendaQ.data?.items.find(it => it.kind === 'event' && String(it.event?.id) === openEventId)?.event ?? null
  }, [openEventId, deepLinkDone, agendaQ.data])
  const shownEvent = editingEvent ?? deepLinkEvent

  const deleteEventMut = useMutation({
    mutationFn: (id: number) => deleteCalEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] }),
  })

  function onSlotClick(day: Date, hour: number) {
    const s = new Date(day); s.setHours(hour, 0, 0, 0)
    const e = new Date(s); e.setHours(hour + 1, 0, 0, 0)
    setCreating({ start: s, end: e })
  }

  const isLoading = agendaQ.isLoading
  const isFetching = agendaQ.isFetching

  // All-day strip: one row per item on the busiest day.
  const allDayByDay = useMemo(() => {
    const m: Record<string, AgendaItem[]> = {}
    for (const it of allDay) {
      const s = parseDt(it.start_at)
      const e = it.end_at ? parseDt(it.end_at) : s
      for (const d of days) {
        if (startOfDay(d) >= startOfDay(s) && startOfDay(d) <= startOfDay(e)) (m[ymd(d)] ??= []).push(it)
      }
    }
    return m
  }, [allDay, days])
  const allDayRows = Math.min(4, Math.max(0, ...Object.values(allDayByDay).map(l => l.length)))
  const gridTop = HEADER_H + (allDayRows > 0 ? allDayRows * ALLDAY_ROW_H + 8 : 0)

  // Browser alarms for events + scheduled stories visible this week.
  const alarmItems = useMemo(() => [
    ...events.map(e => ({ id: `ev:${e.id}`, title: e.title, start_at: e.start_at })),
    ...stories.map(b => ({ id: `st:${b.session.id}`, title: `${b.story.key} — ${b.story.title}`, start_at: b.session.start_at })),
  ], [events, stories])
  useEventAlarms(alarmItems)

  return (
    <div className="relative">
      {isFetching && !isLoading && (
        <div className="absolute top-2 right-3 z-40 flex items-center gap-1.5 text-[10px] text-fg-subtle bg-bg-elevated border border-border rounded-full px-2 py-1 shadow-sm">
          <span className="h-2 w-2 rounded-full border border-accent border-t-transparent animate-spin" />
          syncing
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: '50px repeat(7, 1fr)' }}>
        {/* Header row */}
        <div className="sticky top-0 z-30 bg-bg-elevated border-r border-b border-border" style={{ height: HEADER_H }} />
        {days.map((d, i) => {
          const isToday = sameDay(d, new Date())
          return (
            <div
              key={i}
              className={cn(
                'sticky top-0 z-30 border-r last:border-r-0 border-b border-border bg-bg-elevated px-2 py-1.5',
                isToday && 'bg-highlight/30',
              )}
              style={{ height: HEADER_H }}
            >
              <div className="text-[10px] uppercase tracking-wider text-fg-subtle">
                {d.toLocaleDateString(undefined, { weekday: 'short' })}
              </div>
              <div className={cn('text-lg font-semibold tabular-nums leading-tight', isToday && 'text-accent')}>
                {d.getDate()}
              </div>
            </div>
          )
        })}

        {/* All-day strip */}
        {allDayRows > 0 && (
          <>
            <div
              className="sticky z-30 text-[9px] uppercase tracking-wider text-fg-subtle pr-1.5 pt-1 text-right border-r border-b border-border bg-bg-elevated"
              style={{ top: HEADER_H }}
            >
              all day
            </div>
            {days.map((d, i) => {
              const list = allDayByDay[ymd(d)] ?? []
              return (
                <div
                  key={i}
                  className="sticky z-30 border-r last:border-r-0 border-b border-border bg-bg-elevated px-1 py-1 space-y-0.5 overflow-hidden"
                  style={{ top: HEADER_H, height: allDayRows * ALLDAY_ROW_H + 8 }}
                >
                  {list.slice(0, allDayRows).map((it, j) => (
                    <AllDayPill
                      key={it.id + j}
                      item={it}
                      more={j === allDayRows - 1 && list.length > allDayRows ? list.length - allDayRows : 0}
                      onClick={() => {
                        if (it.kind === 'event' && it.event) setEditingEvent(it.event)
                        else if (it.story) setEditingStory({ story: it.story, session: it.session ?? null })
                        else if (it.google?.html_link) window.open(it.google.html_link, '_blank', 'noreferrer')
                      }}
                    />
                  ))}
                </div>
              )
            })}
          </>
        )}

        {/* Hour rows */}
        {HOURS.map(h => (
          <Fragment key={h}>
            <div className="text-[10px] text-fg-subtle font-mono pr-2 pt-0.5 text-right border-r border-border">
              {fmtHour(h)}
            </div>
            {days.map((d, di) => (
              <DaySlot key={`${h}-${di}`} day={d} hour={h} onClick={() => onSlotClick(d, h)} />
            ))}
          </Fragment>
        ))}
      </div>

      <NowLine days={days} top={gridTop} />

      {/* Google Calendar events (read-only) */}
      <GCalLayer days={days} items={google} top={gridTop} lanes={lanes} />

      {/* Event layer — absolutely positioned over the grid */}
      <BlockLayer top={gridTop}>
        {(containerRef) => events
          .filter(e => days.some(d => sameDay(parseDt(e.start_at), d)))
          .map(ev => (
            <DraggableEvent
              key={ev.id}
              event={ev}
              lane={lanes[`event:${ev.id}`]}
              color={ev._color}
              weekStart={days[0]}
              containerRef={containerRef}
              onUpdate={(patch) => updateMut.mutate({ id: ev.id, patch })}
              onDelete={() => setPendingDeleteEvent(ev)}
              onClick={() => setEditingEvent(ev)}
            />
          ))}
      </BlockLayer>

      {/* Scheduled stories layer */}
      <BlockLayer top={gridTop}>
        {(containerRef) => stories
          .filter(b => days.some(d => sameDay(parseDt(b.session.start_at), d)))
          .map(b => (
            <DraggableStory
              key={b.itemId}
              block={b}
              lane={lanes[b.itemId]}
              weekStart={days[0]}
              containerRef={containerRef}
              onUpdate={(patch) => storyMut.mutate({ block: b, patch })}
              onClick={() => setEditingStory({ story: b.story, session: b.session })}
            />
          ))}
      </BlockLayer>

      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-bg-elevated/60 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <span className="h-4 w-4 rounded-full border-2 border-border border-t-accent animate-spin" />
            Loading your week…
          </div>
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="absolute inset-x-0 z-10 flex justify-center pointer-events-none" style={{ top: gridTop + 40 }}>
          <div className="text-xs text-fg-subtle italic px-3 py-2 rounded bg-bg-elevated/80 border border-border">
            Nothing scheduled this week. Click any slot to add something.
          </div>
        </div>
      )}

      {creating && (
        <CreateEventDrawer
          projects={projects}
          defaultProjectId={defaultProjectId}
          slot={creating}
          onClose={() => setCreating(null)}
          onCreated={() => invalidateSchedule(qc)}
        />
      )}

      {shownEvent && (
        <EditEventDrawer
          key={shownEvent.id}
          event={shownEvent}
          onClose={() => { setEditingEvent(null); setDeepLinkDone(true) }}
          onSaved={() => qc.invalidateQueries({ queryKey: ['agenda'] })}
        />
      )}

      {editingStory && (
        <EditStoryDrawer
          key={`${editingStory.story.id}:${editingStory.session?.id ?? 'new'}`}
          target={editingStory}
          onClose={() => setEditingStory(null)}
          onSaved={() => invalidateSchedule(qc)}
        />
      )}

      <ConfirmDialog
        open={pendingDeleteEvent !== null}
        onOpenChange={(o) => { if (!o) setPendingDeleteEvent(null) }}
        title="Delete event?"
        description={pendingDeleteEvent ? `"${pendingDeleteEvent.title}" will be removed from the calendar.` : undefined}
        onConfirm={() => { if (pendingDeleteEvent) deleteEventMut.mutate(pendingDeleteEvent.id) }}
      />
    </div>
  )
}

function DaySlot({ day, hour, onClick }: { day: Date; hour: number; onClick: () => void }) {
  const isToday = sameDay(day, new Date())
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative border-r last:border-r-0 border-b border-border',
        'hover:bg-accent-soft/30 transition-colors',
        isToday && 'bg-highlight/10',
      )}
      style={{ height: HOUR_HEIGHT }}
      aria-label={`${day.toDateString()} ${hour}:00`}
    >
      {/* half-hour guide */}
      <span className="absolute left-0 right-0 top-1/2 border-t border-dashed border-border/60 pointer-events-none" />
      <span className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-fg-subtle">
        <Plus size={10} />
      </span>
    </button>
  )
}

function AllDayPill({ item, more, onClick }: { item: AgendaItem; more: number; onClick: () => void }) {
  const color = item.project?.color ?? item.color ?? (item.kind === 'google' ? '#4285f4' : '#e8c468')
  return (
    <button
      onClick={onClick}
      title={item.title}
      className="w-full h-[18px] flex items-center gap-1 rounded px-1 text-[10px] leading-none truncate hover:brightness-95"
      style={{ backgroundColor: `${color}30`, color: '#1a1a1a' }}
    >
      {item.kind === 'story_due' && <span style={{ color }}>◆</span>}
      {item.kind === 'google' && <Lock size={8} className="shrink-0 text-fg-subtle" />}
      <span className="truncate">{item.title}</span>
      {more > 0 && <span className="ml-auto text-fg-subtle font-mono shrink-0">+{more}</span>}
    </button>
  )
}

function NowLine({ days, top }: { days: Date[]; top: number }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])
  const di = days.findIndex(d => sameDay(d, now))
  const min = (now.getHours() - VIEW_START_HOUR) * 60 + now.getMinutes()
  if (di < 0 || min < 0 || min > HOURS.length * 60) return null
  return (
    <div className="absolute pointer-events-none" style={{ left: 51, right: 0, top: top + (min / 60) * HOUR_HEIGHT, zIndex: 40 }}>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((_, i) => (
          <div key={i} className={cn('h-0.5', i === di ? 'bg-danger' : 'bg-transparent')} />
        ))}
      </div>
    </div>
  )
}

/** Absolutely-positioned overlay aligned to the day columns of the hour grid. */
function BlockLayer({ top, children }: {
  top: number
  children: (containerRef: React.RefObject<HTMLDivElement | null>) => React.ReactNode
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  return (
    <div
      ref={containerRef}
      className="absolute pointer-events-none"
      style={{ left: 51, right: 0, top, height: GRID_H }}
    >
      {children(containerRef)}
    </div>
  )
}

/**
 * Pixel geometry for a block, clamped to the visible hours so an 6am or
 * 10pm item still shows at the grid's edge instead of off-canvas.
 */
function blockGeom(start: Date, end: Date): { top: number; height: number } {
  const startMin = (start.getHours() - VIEW_START_HOUR) * 60 + start.getMinutes()
  const durMin   = Math.max(15, (end.getTime() - start.getTime()) / 60_000)
  let top    = (startMin / 60) * HOUR_HEIGHT
  let height = (durMin / 60) * HOUR_HEIGHT
  if (top < 0) { height += top; top = 0 }
  height = Math.max(MIN_BLOCK, height)
  if (top > GRID_H - MIN_BLOCK) top = GRID_H - MIN_BLOCK
  if (top + height > GRID_H) height = GRID_H - top
  return { top, height }
}

/** Pointer-drag move + bottom-edge resize, shared by events and stories. */
function useBlockDrag({ start, end, containerRef, onMove, onResize, onClick, disabled }: {
  start: Date
  end: Date
  containerRef: React.RefObject<HTMLDivElement | null>
  onMove: (ns: Date, ne: Date) => void
  onResize: (ne: Date) => void
  onClick?: () => void
  disabled?: boolean
}) {
  const moveState = useRef<{ originX: number; originY: number; dxDays: number; dyMin: number } | null>(null)
  const resizeState = useRef<{ originY: number; dyMin: number } | null>(null)
  const [moveDelta, setMoveDelta] = useState<{ dx: number; dy: number } | null>(null)
  const [resizeDy, setResizeDy] = useState<number | null>(null)

  const dayWidth = useCallback((): number => {
    const w = containerRef.current?.getBoundingClientRect().width ?? 700
    return w / 7
  }, [containerRef])

  const snap = (dy: number) => Math.round((dy / HOUR_HEIGHT) * 60 / SNAP_MIN) * SNAP_MIN

  const move = {
    onPointerDown: (e: React.PointerEvent) => {
      if (disabled) return
      const role = (e.target as HTMLElement).dataset.role
      if (role === 'resize' || role === 'action') return
      e.stopPropagation()
      moveState.current = { originX: e.clientX, originY: e.clientY, dxDays: 0, dyMin: 0 }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!moveState.current) return
      const dxDays = Math.round((e.clientX - moveState.current.originX) / dayWidth())
      const dyMin = snap(e.clientY - moveState.current.originY)
      moveState.current.dxDays = dxDays
      moveState.current.dyMin = dyMin
      setMoveDelta({ dx: dxDays * dayWidth(), dy: (dyMin / 60) * HOUR_HEIGHT })
    },
    onPointerUp: () => {
      if (!moveState.current) { if (disabled) onClick?.(); return }
      const { dxDays, dyMin } = moveState.current
      moveState.current = null
      setMoveDelta(null)
      if (dxDays === 0 && dyMin === 0) { onClick?.(); return }
      const ns = new Date(start); ns.setDate(ns.getDate() + dxDays); ns.setMinutes(ns.getMinutes() + dyMin)
      const ne = new Date(end);   ne.setDate(ne.getDate() + dxDays); ne.setMinutes(ne.getMinutes() + dyMin)
      onMove(ns, ne)
    },
  }

  const resize = {
    onPointerDown: (e: React.PointerEvent) => {
      if (disabled) return
      e.stopPropagation()
      resizeState.current = { originY: e.clientY, dyMin: 0 }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!resizeState.current) return
      const dyMin = snap(e.clientY - resizeState.current.originY)
      resizeState.current.dyMin = dyMin
      setResizeDy((dyMin / 60) * HOUR_HEIGHT)
    },
    onPointerUp: () => {
      if (!resizeState.current) return
      const dyMin = resizeState.current.dyMin
      resizeState.current = null
      setResizeDy(null)
      if (dyMin === 0) return
      const ne = new Date(end.getTime() + dyMin * 60_000)
      if (ne.getTime() < start.getTime() + SNAP_MIN * 60_000) return
      onResize(ne)
    },
  }

  return { move, resize, moveDelta, resizeDy, dayWidth, dragging: moveDelta !== null || resizeDy !== null }
}

function laneWidth(lane: Lane | undefined, dayW: number, dragging: boolean): number {
  // While dragging, a block takes the full column so it reads clearly.
  return lane && !dragging ? dayW / lane.lanes : dayW
}
function laneOffset(lane: Lane | undefined, dayW: number): number {
  return lane ? (dayW / lane.lanes) * lane.lane : 0
}

const TYPE_COLORS: Record<CalEventType, string> = {
  meeting:   '#6b9bd1',
  focus:     '#d97757',
  due:       '#e8c468',
  milestone: '#8aa572',
}

function DraggableEvent({
  event,
  lane,
  color,
  weekStart,
  containerRef,
  onUpdate,
  onDelete,
  onClick,
}: {
  event: CalEvent
  lane?: Lane
  color: string | null
  weekStart: Date
  containerRef: React.RefObject<HTMLDivElement | null>
  onUpdate: (patch: { start_at?: string; end_at?: string | null }) => void
  onDelete: () => void
  onClick: () => void
}) {
  const start = parseDt(event.start_at)
  const end   = event.end_at ? parseDt(event.end_at) : new Date(start.getTime() + 60 * 60_000)
  const dayIndex = dayDiff(weekStart, start)
  const { top, height } = blockGeom(start, end)
  const drag = useBlockDrag({
    start, end, containerRef, onClick,
    onMove: (ns, ne) => onUpdate({ start_at: dtFmt(ns), end_at: dtFmt(ne) }),
    onResize: (ne) => onUpdate({ end_at: dtFmt(ne) }),
  })

  const ring = color ?? TYPE_COLORS[event.type]
  const liveHeight = Math.max(MIN_BLOCK, height + (drag.resizeDy ?? 0))
  const compact = liveHeight < 44

  return (
    <div
      className={cn(
        'absolute select-none border-l-[3px] rounded-md px-2 text-[11px] leading-snug overflow-hidden shadow-sm pointer-events-auto group cursor-grab',
        compact ? 'py-0.5 flex items-center gap-1.5' : 'py-1',
      )}
      style={{
        left: dayIndex * drag.dayWidth() + laneOffset(lane, drag.dayWidth()) + (drag.moveDelta?.dx ?? 0) + 2,
        width: laneWidth(lane, drag.dayWidth(), drag.dragging) - 4,
        top: top + (drag.moveDelta?.dy ?? 0),
        height: liveHeight,
        backgroundColor: `${ring}2e`,
        borderLeftColor: ring,
        color: '#1a1a1a',
        zIndex: drag.dragging ? 50 : 10,
        outline: drag.dragging ? '2px solid var(--color-accent)' : undefined,
      }}
      {...drag.move}
    >
      <button
        type="button"
        data-role="action"
        onPointerDown={(e) => { e.stopPropagation(); onDelete() }}
        title="Delete event"
        className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 rounded hover:bg-bg-elevated/80 p-0.5 z-10"
      >
        <span data-role="action" className="text-[11px] leading-none text-fg-subtle hover:text-danger">×</span>
      </button>
      {compact ? (
        <>
          <span className="font-mono text-[9px] text-fg-subtle shrink-0">{fmtTime(start)}</span>
          {event.project_key && <ProjectTag k={event.project_key} color={ring} />}
          <span className="font-medium truncate">{event.title}</span>
        </>
      ) : (
        <>
          <div className="font-mono text-[9px] uppercase tracking-wider text-fg-subtle flex items-center gap-1">
            {event.project_key && <ProjectTag k={event.project_key} color={ring} />}
            {event.type}{event.type === 'due' && ' ◆'}
          </div>
          <div className="font-medium truncate">{event.title}</div>
          <div className="font-mono text-[9px] text-fg-subtle">{fmtTime(start)}–{fmtTime(end)}</div>
        </>
      )}
      <div
        data-role="resize"
        {...drag.resize}
        className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize hover:bg-accent/30"
      />
    </div>
  )
}

function ProjectTag({ k, color }: { k: string; color: string }) {
  return (
    <span className="px-1 rounded text-[8px] font-bold leading-[12px] shrink-0" style={{ backgroundColor: color, color: '#fff' }}>
      {k}
    </span>
  )
}

type SessionPatch = { start_at?: string; end_at?: string }

/** One work session of a story; a multi-day story is several of these. */
function DraggableStory({
  block,
  lane,
  weekStart,
  containerRef,
  onUpdate,
  onClick,
}: {
  block: StoryBlock
  lane?: Lane
  weekStart: Date
  containerRef: React.RefObject<HTMLDivElement | null>
  /** null removes this session. */
  onUpdate: (patch: SessionPatch | null) => void
  onClick: () => void
}) {
  const story = block.story
  const start = parseDt(block.session.start_at)
  const end = parseDt(block.session.end_at)
  const dayIndex = dayDiff(weekStart, start)
  const { top, height } = blockGeom(start, end)
  const drag = useBlockDrag({
    start, end, containerRef, onClick,
    onMove: (ns, ne) => onUpdate({ start_at: dtFmt(ns), end_at: dtFmt(ne) }),
    onResize: (ne) => onUpdate({ end_at: dtFmt(ne) }),
  })
  const multi = block.session.count > 1

  const ring = story.project_color ?? story.epic_color ?? '#7e57c2'
  const liveHeight = Math.max(MIN_BLOCK, height + (drag.resizeDy ?? 0))
  const compact = liveHeight < 44

  return (
    <div
      className={cn(
        'absolute select-none border-l-[3px] border-dashed rounded-md px-2 text-[11px] leading-snug overflow-hidden shadow-sm cursor-grab pointer-events-auto group',
        compact ? 'py-0.5 flex items-center gap-1.5' : 'py-1',
      )}
      style={{
        left: dayIndex * drag.dayWidth() + laneOffset(lane, drag.dayWidth()) + (drag.moveDelta?.dx ?? 0) + 2,
        width: laneWidth(lane, drag.dayWidth(), drag.dragging) - 4,
        top: top + (drag.moveDelta?.dy ?? 0),
        height: liveHeight,
        backgroundColor: `${ring}1f`,
        borderLeftColor: ring,
        color: '#1a1a1a',
        zIndex: drag.dragging ? 50 : 11,
        outline: drag.dragging ? '2px solid var(--color-accent)' : undefined,
      }}
      {...drag.move}
    >
      <button
        type="button"
        data-role="action"
        onPointerDown={(e) => { e.stopPropagation(); onUpdate(null) }}
        title={multi ? 'Remove this session (the story keeps its other sessions)' : 'Unschedule (remove from calendar)'}
        className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 rounded hover:bg-bg-elevated/80 p-0.5 z-10"
      >
        <span data-role="action" className="text-[11px] leading-none text-fg-subtle hover:text-danger">×</span>
      </button>
      {compact ? (
        <>
          <span className="font-mono text-[9px] text-fg-subtle shrink-0">{story.key}</span>
          <span className="font-medium truncate">{story.title}</span>
        </>
      ) : (
        <>
          <div className="font-mono text-[9px] uppercase tracking-wider text-fg-subtle">
            {story.key} · {story.status_category.replace('_', ' ')}{multi && ` · ${block.session.count} sessions`}
          </div>
          <div className="font-medium truncate">{story.title}</div>
          <div className="font-mono text-[9px] text-fg-subtle">{fmtTime(start)}–{fmtTime(end)}</div>
        </>
      )}
      <div
        data-role="resize"
        {...drag.resize}
        className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize hover:bg-accent/30"
      />
    </div>
  )
}

function GCalLayer({ days, items, top: layerTop, lanes }: {
  days: Date[]; items: AgendaItem[]; top: number; lanes: Record<string, Lane>
}) {
  return (
    <div className="absolute pointer-events-none" style={{ left: 51, right: 0, top: layerTop, height: GRID_H }}>
      <div className="grid h-full" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((d, di) => (
          <div key={di} className="relative">
            {items
              .filter(it => sameDay(parseDt(it.start_at), d))
              .map((it, ei) => {
                const start = parseDt(it.start_at)
                const end = it.end_at ? parseDt(it.end_at) : new Date(start.getTime() + 30 * 60_000)
                const { top, height } = blockGeom(start, end)
                const ring = it.color ?? '#4285f4'
                const link = it.google?.html_link
                const l = lanes[it.id]
                return (
                  <div
                    key={it.id + '-' + ei}
                    onClick={() => { if (link) window.open(link, '_blank', 'noreferrer') }}
                    title={`${it.title} · ${it.google?.calendar_name ?? 'Google Calendar'} (read-only — click to open in Google)`}
                    className="absolute rounded-md pointer-events-auto select-none border-l-[3px] px-2 py-0.5 text-[11px] leading-snug overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    style={{
                      top, height,
                      left: `calc(${l ? (100 / l.lanes) * l.lane : 0}% + 2px)`,
                      width: `calc(${l ? 100 / l.lanes : 100}% - 4px)`,
                      backgroundColor: `${ring}1c`, borderLeftColor: ring, color: '#1a1a1a', zIndex: 9,
                    }}
                  >
                    <div className="flex items-center gap-1 font-medium truncate">
                      <Lock size={8} className="shrink-0 text-fg-subtle" />
                      <span className="truncate">{it.title}</span>
                    </div>
                    {height >= 44 && (
                      <div className="font-mono text-[9px] text-fg-subtle">{fmtTime(start)}–{fmtTime(end)}</div>
                    )}
                  </div>
                )
              })}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Month grid ───────────────────────────────────────────────────────────

function MonthGrid({ cursor, isVisible, onPickDay }: {
  cursor: Date
  isVisible: (item: AgendaItem) => boolean
  onPickDay: (d: Date) => void
}) {
  const monthStart = startOfMonth(cursor)
  const monthEnd   = endOfMonth(cursor)
  const gridStart  = weekStart(monthStart)
  const gridLast   = addDays(weekStart(monthEnd), 6)
  const cells: Date[] = []
  for (let d = new Date(gridStart); d <= gridLast; d = addDays(d, 1)) cells.push(new Date(d))

  const agendaQ = useAgenda(gridStart, gridLast)
  const byDay = useMemo(() => {
    const m: Record<string, AgendaItem[]> = {}
    for (const it of agendaQ.data?.items ?? []) {
      if (!isVisible(it)) continue
      ;(m[it.start_at.slice(0, 10)] ??= []).push(it)
    }
    return m
  }, [agendaQ.data, isVisible])

  const isLoading = agendaQ.isLoading

  return (
    <div className="p-6 relative">
      {isLoading && (
        <div className="absolute inset-6 z-20 flex items-center justify-center bg-bg-elevated/60 backdrop-blur-[1px] rounded-xl">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <span className="h-4 w-4 rounded-full border-2 border-border border-t-accent animate-spin" />
            Loading month…
          </div>
        </div>
      )}
      <Card padded={false} className="overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-bg-muted">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-fg-subtle text-center py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth()
            const isToday = sameDay(d, new Date())
            const list = byDay[ymd(d)] ?? []
            const isWeekend = d.getDay() === 0 || d.getDay() === 6
            return (
              <button
                key={i}
                onClick={() => onPickDay(d)}
                className={cn(
                  'relative text-left border-r [&:nth-child(7n)]:border-r-0 border-b border-border p-1.5 min-h-[120px] hover:bg-bg-muted/40 align-top',
                  !inMonth && 'opacity-40',
                  isWeekend && inMonth && 'bg-bg-muted/40',
                  isToday && 'bg-highlight/30',
                )}
              >
                <div className={cn('text-xs font-mono mb-1', isToday ? 'text-accent font-bold' : 'text-fg-subtle')}>
                  {d.getDate()}
                </div>
                <div className="space-y-0.5">
                  {list.slice(0, 4).map(it => <MonthPill key={it.id} item={it} />)}
                  {list.length > 4 && (
                    <div className="text-[10px] font-mono text-fg-subtle px-1">+{list.length - 4} more</div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

function MonthPill({ item }: { item: AgendaItem }) {
  const color = item.project?.color ?? item.color
    ?? (item.kind === 'google' ? '#4285f4' : item.event ? TYPE_COLORS[item.event.type] : '#7e57c2')
  const time = item.all_day ? null : fmtTime(parseDt(item.start_at))
  return (
    <div
      className="px-1.5 py-0.5 rounded text-[10px] truncate flex items-center gap-1"
      style={{ backgroundColor: `${color}26`, color: '#1a1a1a' }}
      title={item.title}
    >
      {item.kind === 'story_due'
        ? <span style={{ color }}>◆</span>
        : <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />}
      {time && <span className="font-mono text-fg-subtle shrink-0">{time}</span>}
      <span className="truncate">{item.title}</span>
    </div>
  )
}

// ─── Create-event drawer ──────────────────────────────────────────────────

function CreateEventDrawer({
  projects,
  defaultProjectId,
  slot,
  onClose,
  onCreated,
}: {
  projects: ProjectLookup[]
  defaultProjectId: number | null
  slot: { start: Date; end: Date }
  onClose: () => void
  onCreated: () => void
}) {
  const [mode, setMode] = useState<'event' | 'story'>('event')
  const [title, setTitle] = useState('')
  const [type, setType]   = useState<CalEventType>('focus')
  const [projectId, setProjectId] = useState<number | null>(defaultProjectId)
  const [endStr, setEndStr] = useState(timeOnly(slot.end))
  const [search, setSearch] = useState('')
  const [pickedStoryId, setPickedStoryId] = useState<number | null>(null)

  // Story mode: that project's open stories, or — with no project — the
  // open stories assigned to me anywhere.
  const openStoriesQ = useQuery({
    queryKey: ['stories', 'open-for-schedule', projectId ?? 'mine'],
    queryFn: async () => projectId
      ? (await listStories({ project_id: projectId, per_page: 200, sort: '-updated_at' })).data
      : await listMyStories({ per_page: 200 }),
    enabled: mode === 'story',
    staleTime: 10_000,
  })
  const openStories = useMemo(
    // Already-scheduled stories stay pickable: this adds another session.
    () => (openStoriesQ.data ?? []).filter(s => s.status_category !== 'done'),
    [openStoriesQ.data],
  )
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return openStories.slice(0, 50)
    return openStories.filter(s =>
      s.key.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)
    ).slice(0, 50)
  }, [openStories, search])

  const computeEnd = (): Date => {
    const endDate = new Date(slot.start)
    const [hh, mm] = endStr.split(':').map(Number)
    endDate.setHours(hh ?? 0, mm ?? 0, 0, 0)
    return endDate > slot.start ? endDate : new Date(slot.start.getTime() + 60 * 60_000)
  }

  const createEventMut = useMutation({
    mutationFn: () => createCalEvent({
      project_id: projectId,
      type,
      title: title.trim() || `${type} block`,
      start_at: dtFmt(slot.start),
      end_at: dtFmt(computeEnd()),
    }),
    onSuccess: () => { onCreated(); onClose() },
  })

  const scheduleStoryMut = useMutation({
    mutationFn: () => {
      if (!pickedStoryId) throw new Error('No story selected')
      return addStorySession(pickedStoryId, {
        start_at: dtFmt(slot.start),
        end_at: dtFmt(computeEnd()),
      })
    },
    onSuccess: () => { onCreated(); onClose() },
  })

  const submit = () => {
    if (mode === 'event') createEventMut.mutate()
    else scheduleStoryMut.mutate()
  }
  const submitting = createEventMut.isPending || scheduleStoryMut.isPending
  const canSubmit = mode === 'event' ? true : !!pickedStoryId
  const error = (createEventMut.error ?? scheduleStoryMut.error) as Error | null

  return (
    <Drawer
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      title={mode === 'event' ? 'New event' : 'Schedule story'}
      description={`${slot.start.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })} · starts ${fmtTime(slot.start)}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !canSubmit}>
            {submitting ? 'Saving…' : mode === 'event' ? 'Create event' : 'Schedule'}
          </Button>
        </div>
      }
      width="sm"
    >
      <div className="space-y-4">
        <div className="inline-flex rounded-md border border-border overflow-hidden w-full">
          <button
            type="button"
            onClick={() => setMode('event')}
            className={cn('flex-1 px-3 py-1.5 text-xs font-medium', mode === 'event' ? 'bg-accent text-accent-fg' : 'bg-bg-elevated text-fg-muted hover:bg-bg-muted')}
          >
            New event
          </button>
          <button
            type="button"
            onClick={() => setMode('story')}
            className={cn('flex-1 px-3 py-1.5 text-xs font-medium', mode === 'story' ? 'bg-accent text-accent-fg' : 'bg-bg-elevated text-fg-muted hover:bg-bg-muted')}
          >
            Schedule story
          </button>
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            {mode === 'event' ? 'Project' : 'Stories from'}
          </span>
          <select
            value={projectId ?? ''}
            onChange={e => { setProjectId(e.target.value === '' ? null : Number(e.target.value)); setPickedStoryId(null) }}
            className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm"
          >
            <option value="">{mode === 'event' ? 'Personal (only you)' : 'Assigned to me (any project)'}</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.key} — {p.name}</option>)}
          </select>
        </label>

        {mode === 'event' ? (
          <>
            <label className="block">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">Title</span>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                autoFocus
                placeholder={`${type} block`}
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </label>
            <TypePicker type={type} setType={setType} />
          </>
        ) : (
          <>
            <label className="block">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">Search open stories</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
                placeholder="Title or key (e.g. ATL-12)"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </label>
            <div className="max-h-[280px] overflow-y-auto rounded-md border border-border divide-y divide-border">
              {openStoriesQ.isLoading && (
                <div className="px-3 py-6 text-center text-xs text-fg-subtle">Loading…</div>
              )}
              {!openStoriesQ.isLoading && filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-fg-subtle italic">
                  {openStories.length === 0 ? 'No open stories.' : 'No matches.'}
                </div>
              )}
              {filtered.map(s => {
                const picked = pickedStoryId === s.id
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setPickedStoryId(s.id)}
                    className={cn(
                      'w-full text-left px-3 py-2 text-xs flex items-start gap-2 hover:bg-bg-muted',
                      picked && 'bg-accent-soft',
                    )}
                  >
                    <span className="font-mono text-[10px] text-fg-subtle pt-0.5 min-w-[56px]">{s.key}</span>
                    <span className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{s.title}</span>
                      <span className="text-[10px] text-fg-subtle font-mono">
                        {s.status_name} · {s.priority}{s.scheduled_start && ' · scheduled — adds another session'}
                      </span>
                    </span>
                    {s.epic_color && (
                      <span className="h-2 w-2 rounded-full mt-1" style={{ backgroundColor: s.epic_color }} />
                    )}
                  </button>
                )
              })}
            </div>
          </>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">End time</span>
          <input
            type="time"
            step={SNAP_MIN * 60}
            value={endStr}
            onChange={e => setEndStr(e.target.value)}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          />
        </label>
        {error && <p className="text-xs text-danger">{error.message}</p>}
      </div>
    </Drawer>
  )
}

function TypePicker({ type, setType }: { type: CalEventType; setType: (t: CalEventType) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-fg-muted mb-1.5">Type</label>
      <div className="grid grid-cols-4 gap-2">
        {(['focus', 'meeting', 'due', 'milestone'] as CalEventType[]).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={cn(
              'rounded-md border text-xs py-2 capitalize',
              type === t ? 'border-accent bg-accent-soft text-accent-hover' : 'border-border hover:bg-bg-muted',
            )}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Edit drawers ─────────────────────────────────────────────────────────

function EditEventDrawer({
  event,
  onClose,
  onSaved,
}: {
  event: CalEvent
  onClose: () => void
  onSaved: () => void
}) {
  const start = parseDt(event.start_at)
  const end = event.end_at ? parseDt(event.end_at) : new Date(start.getTime() + 60 * 60_000)
  const [title, setTitle] = useState(event.title)
  const [type, setType] = useState<CalEventType>(event.type)
  const [dateStr, setDateStr] = useState(ymd(start))
  const [startStr, setStartStr] = useState(timeOnly(start))
  const [endStr, setEndStr] = useState(timeOnly(end))
  const [reminder, setReminder] = useState<number | null>(getEventLeadMin(`ev:${event.id}`))
  const [confirmDelete, setConfirmDelete] = useState(false)

  const saveMut = useMutation({
    mutationFn: () => {
      const [sh, sm] = startStr.split(':').map(Number)
      const [eh, em] = endStr.split(':').map(Number)
      const [yy, mm, dd] = dateStr.split('-').map(Number)
      const ns = new Date(yy, (mm ?? 1) - 1, dd ?? 1, sh ?? 0, sm ?? 0)
      const ne = new Date(yy, (mm ?? 1) - 1, dd ?? 1, eh ?? 0, em ?? 0)
      setEventLeadMin(`ev:${event.id}`, reminder)
      return updateCalEvent(event.id, {
        title: title.trim() || `${type} block`,
        type,
        start_at: dtFmt(ns),
        end_at: dtFmt(ne > ns ? ne : new Date(ns.getTime() + 30 * 60_000)),
      })
    },
    onSuccess: () => { onSaved(); onClose() },
  })
  const deleteMut = useMutation({
    mutationFn: () => deleteCalEvent(event.id),
    onSuccess: () => { onSaved(); onClose() },
  })

  return (
    <Drawer
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      title="Edit event"
      description={event.project_key ? `${event.project_key} · ${event.project_name}` : 'Personal event'}
      footer={
        <div className="flex justify-between items-center gap-2 w-full">
          <Button variant="outline" onClick={() => setConfirmDelete(true)} disabled={deleteMut.isPending}>
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      }
      width="sm"
    >
      <div className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Title</span>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          />
        </label>
        <TypePicker type={type} setType={setType} />
        <DateTimeFields
          dateStr={dateStr} setDateStr={setDateStr}
          startStr={startStr} setStartStr={setStartStr}
          endStr={endStr} setEndStr={setEndStr}
        />

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Reminder</span>
          <select
            value={reminder ?? ''}
            onChange={e => setReminder(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm"
          >
            <option value="">None</option>
            <option value={0}>At start</option>
            <option value={5}>5 min before</option>
            <option value={10}>10 min before</option>
            <option value={15}>15 min before</option>
            <option value={30}>30 min before</option>
            <option value={60}>1 hour before</option>
          </select>
          <span className="block text-[10px] text-fg-subtle mt-1">
            Browser notification while Atlas is open. Persists across reloads.
          </span>
        </label>
      </div>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete event?"
        description={`"${event.title}" will be removed from the calendar.`}
        onConfirm={() => deleteMut.mutate()}
      />
    </Drawer>
  )
}

function DateTimeFields({ dateStr, setDateStr, startStr, setStartStr, endStr, setEndStr }: {
  dateStr: string; setDateStr: (v: string) => void
  startStr: string; setStartStr: (v: string) => void
  endStr: string; setEndStr: (v: string) => void
}) {
  const input = 'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'
  return (
    <>
      <label className="block">
        <span className="block text-xs font-medium text-fg-muted mb-1.5">Date</span>
        <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} className={input} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Start time</span>
          <input type="time" step={SNAP_MIN * 60} value={startStr} onChange={e => setStartStr(e.target.value)} className={input} />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">End time</span>
          <input type="time" step={SNAP_MIN * 60} value={endStr} onChange={e => setEndStr(e.target.value)} className={input} />
        </label>
      </div>
    </>
  )
}

/**
 * A story on the calendar: edits the session that was clicked (or schedules
 * one, when opened from a due marker), lists every session of the story so
 * a multi-day story can be seen and trimmed in one place, and can add the
 * next session.
 */
function EditStoryDrawer({
  target,
  onClose,
  onSaved,
}: {
  target: StoryTarget
  onClose: () => void
  onSaved: () => void
}) {
  const { story, session } = target
  const qc = useQueryClient()
  const { user } = useAuth()
  const canDelete = !!user && user.role !== 'viewer'
  const start = session ? parseDt(session.start_at) : nextHour()
  const end = session ? parseDt(session.end_at) : new Date(start.getTime() + 60 * 60_000)
  const [title, setTitle] = useState(story.title)
  const [dateStr, setDateStr] = useState(ymd(start))
  const [startStr, setStartStr] = useState(timeOnly(start))
  const [endStr, setEndStr] = useState(timeOnly(end))
  const [confirmDelete, setConfirmDelete] = useState(false)

  const sessionsQ = useQuery({
    queryKey: ['story-sessions', story.id],
    queryFn: () => listStorySessions(story.id),
  })
  const sessions = sessionsQ.data ?? []
  const refresh = () => { qc.invalidateQueries({ queryKey: ['story-sessions', story.id] }); onSaved() }

  function windowFromFields(): { start_at: string; end_at: string } {
    const [sh, sm] = startStr.split(':').map(Number)
    const [eh, em] = endStr.split(':').map(Number)
    const [yy, mm, dd] = dateStr.split('-').map(Number)
    const ns = new Date(yy, (mm ?? 1) - 1, dd ?? 1, sh ?? 0, sm ?? 0)
    const ne = new Date(yy, (mm ?? 1) - 1, dd ?? 1, eh ?? 0, em ?? 0)
    return { start_at: dtFmt(ns), end_at: dtFmt(ne > ns ? ne : new Date(ns.getTime() + 30 * 60_000)) }
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      if (title.trim() && title.trim() !== story.title) await updateStory(story.id, { title: title.trim() })
      const win = windowFromFields()
      return session ? updateStorySession(story.id, session.id, win) : addStorySession(story.id, win)
    },
    onSuccess: () => { refresh(); onClose() },
  })
  // "Add another session": the same time on the next weekday after the
  // story's last session, to be dragged wherever it belongs.
  const addNextMut = useMutation({
    mutationFn: () => {
      const last = sessions.length ? sessions[sessions.length - 1] : null
      const ls = last ? parseDt(last.start_at) : parseDt(windowFromFields().start_at)
      const le = last ? parseDt(last.end_at) : parseDt(windowFromFields().end_at)
      const step = nextWeekdayGap(ls)
      return addStorySession(story.id, {
        start_at: dtFmt(new Date(ls.getTime() + step)),
        end_at: dtFmt(new Date(le.getTime() + step)),
      })
    },
    onSuccess: refresh,
  })
  const removeMut = useMutation({
    mutationFn: (id: number) => deleteStorySession(story.id, id),
    onSuccess: (_d, id) => { refresh(); if (session && id === session.id) onClose() },
  })
  const deleteMut = useMutation({
    mutationFn: () => deleteStory(story.id),
    onSuccess: () => { onSaved(); onClose() },
  })
  const error = (saveMut.error ?? addNextMut.error ?? removeMut.error ?? deleteMut.error) as Error | null

  return (
    <Drawer
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      title={session ? 'Edit work session' : 'Schedule a work session'}
      description={`${story.key} · ${story.status_name}${story.due_date ? ` · due ${story.due_date}` : ''}`}
      footer={
        <div className="flex justify-between items-center gap-2 w-full">
          <div className="flex gap-2">
            {session && (
              <Button variant="outline" onClick={() => removeMut.mutate(session.id)} disabled={removeMut.isPending}>
                {sessions.length > 1 ? 'Remove session' : 'Unschedule'}
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} disabled={deleteMut.isPending} title="Delete story">
                <Trash2 size={14} className="text-danger" />
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : session ? 'Save' : 'Schedule'}
            </Button>
          </div>
        </div>
      }
      width="sm"
    >
      <div className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Title</span>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          />
        </label>
        <DateTimeFields
          dateStr={dateStr} setDateStr={setDateStr}
          startStr={startStr} setStartStr={setStartStr}
          endStr={endStr} setEndStr={setEndStr}
        />

        <div className="rounded-md border border-border">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-xs font-medium text-fg-muted">
              All sessions{sessions.length ? ` · ${sessions.length}` : ''}
              {sessions.length > 0 && ` · ${fmtSessionTotal(sessions)}`}
            </span>
            <button
              type="button"
              onClick={() => addNextMut.mutate()}
              disabled={addNextMut.isPending || sessionsQ.isLoading}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline disabled:opacity-50"
            >
              <Plus size={12} /> Add another session
            </button>
          </div>
          {sessionsQ.isLoading && <div className="px-3 py-3 text-xs text-fg-subtle">Loading…</div>}
          {!sessionsQ.isLoading && sessions.length === 0 && (
            <div className="px-3 py-3 text-xs text-fg-subtle italic">Not on the calendar yet.</div>
          )}
          <ul className="divide-y divide-border">
            {sessions.map(ss => {
              const a = parseDt(ss.start_at)
              const z = parseDt(ss.end_at)
              const current = session?.id === ss.id
              return (
                <li key={ss.id} className={cn('flex items-center gap-2 px-3 py-1.5 text-xs', current && 'bg-accent-soft/40')}>
                  <span className="font-mono text-fg-subtle w-20 shrink-0">
                    {a.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="flex-1">{fmtTime(a)}–{fmtTime(z)}{current && <span className="text-fg-subtle"> · editing</span>}</span>
                  <button
                    type="button"
                    onClick={() => removeMut.mutate(ss.id)}
                    disabled={removeMut.isPending}
                    aria-label={`Remove the ${a.toLocaleDateString()} session`}
                    className="p-1 rounded text-fg-subtle hover:text-danger hover:bg-danger/10"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <a
          href={`/projects/${story.project_key}/backlog?story=${story.key}`}
          className="block text-xs text-accent hover:underline"
        >
          Open {story.key} in {story.project_key} →
        </a>
        {error && <p className="text-xs text-danger">{error.message}</p>}
      </div>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${story.key}?`}
        description={`"${story.title}" and its subtasks, comments and attachments will be permanently deleted. This cannot be undone.`}
        confirmLabel="Delete story"
        onConfirm={() => deleteMut.mutate()}
      />
    </Drawer>
  )
}

function nextHour(): Date {
  const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return d
}
/** ms from a date to the same time on the next weekday (Fri → Mon). */
function nextWeekdayGap(d: Date): number {
  const dow = d.getDay()
  const days = dow === 5 ? 3 : dow === 6 ? 2 : 1
  return days * 86_400_000
}
function fmtSessionTotal(ss: Array<{ start_at: string; end_at: string }>): string {
  const min = ss.reduce((t, x) => t + (parseDt(x.end_at).getTime() - parseDt(x.start_at).getTime()) / 60_000, 0)
  const h = Math.floor(min / 60), m = Math.round(min % 60)
  return h ? `${h}h${m ? ` ${m}m` : ''} planned` : `${m}m planned`
}

// ─── Date helpers ─────────────────────────────────────────────────────────

function parseDt(s: string): Date {
  return new Date(s.replace(' ', 'T'))
}
function startOfDay(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x
}
function dayDiff(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000)
}
function weekStart(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(x.getDate() - x.getDay()) // Sunday
  return x
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1) }
function endOfMonth(d: Date): Date   { return new Date(d.getFullYear(), d.getMonth() + 1, 0) }
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}
function dtFmt(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day} ${hh}:${mm}:${ss}`
}
function fmtHour(h: number): string {
  const ampm = h >= 12 ? 'p' : 'a'
  const hr = h % 12 === 0 ? 12 : h % 12
  return `${hr}${ampm}`
}
function fmtTime(d: Date): string {
  let h = d.getHours()
  const m = d.getMinutes()
  const ampm = h >= 12 ? 'p' : 'a'
  h = h % 12 === 0 ? 12 : h % 12
  return `${h}${m === 0 ? '' : ':' + String(m).padStart(2, '0')}${ampm}`
}
function timeOnly(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
